import * as amplitude from '@amplitude/analytics-browser'
import type {
    BaseEvent,
    EventOptions,
} from '@amplitude/analytics-core'
import {SessionReplayPlugin} from '@amplitude/plugin-session-replay-browser'
import {TrackingService} from '@shared/services/TrackingService'
import type {
    MetaPixelEventProperties,
    MetaPixelInterface,
    MetaPixelStandardEvent,
} from '@shared/types/MetaPixel'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'
import type {AnalyticsServiceConfig} from '../types/AnalyticsService'

type GtagCommands = Gtag.GtagCommands

type EventNames = Gtag.EventNames

type GtagEventData = Gtag.EventParams | Gtag.CustomParams

// Настройки по-умолчанию.
const defaultConfig: AnalyticsServiceConfig = {
    googleAnalyticsCookieName: 'ga_tid',
    googleAnalyticsCookieLifeTime: 63072000,
    googleAnalyticsClientId: null,
    googleAnalyticsAutoTracking: false,
    // yandexMetrikaClientId: null,
    isClarityEnabled: false,
    isMetaPixelEnabled: false,
    isAmplitudeEnabled: false,

}

// Сервис отправки событий в сервисы сбора аналитики.
export class AnalyticsService {

    // Настройки.
    private static config: AnalyticsServiceConfig = {...defaultConfig}

    // Последний известный URL.
    private static lastUrl: string | null = null

    // Готов ли MS Clarity к отправке событий?
    private static isClarityLoaded: boolean | null = null

    // Готов ли Meta Pixel к отправке событий?
    private static isMetaPixelLoaded: boolean | null = null

    // Готова ли Яндекс Метрика к отправке событий?
    // private static isYandexMetrikaLoaded: boolean | null = null

    // Плагин Session Replay для Amplitude.
    private static amplitudeSessionReplayPlugin: SessionReplayPlugin | null = null

    // Активна ли сейчас запись Session Replay?
    private static isSessionReplayRecording: boolean = false

    // Инициализация глобальных переменных для всех сервисов сбора аналитики.
    static initGlobals(config: Partial<AnalyticsServiceConfig>): void {
        console.log('[Analytics] initGlobals')
        if (config.googleAnalyticsClientId && !window.gtag) {
            window.dataLayer = window.dataLayer ?? []
            // Определяем gtag через window.gtag, чтобы AnalyticsService мог вызывать её до загрузки скрипта.
            // ВАЖНО: используем обычную function (не стрелочную) с объектом arguments.
            // GA4's gtag/js при инициализации перебирает window.dataLayer и ожидает найти объекты Arguments
            // (созданные через arguments), а не обычные массивы (созданные через ...rest params).
            // Если в dataLayer попадают обычные массивы, GA4 не распознаёт их как gtag-команды
            // и не выполняет 'js' / 'config' — скрипт загружается, но ничего не отслеживает.
            window.gtag = function () {
                // eslint-disable-next-line prefer-rest-params
                window.dataLayer!.push(arguments as unknown as Record<string, unknown>)
            }
        }

        if (config.isMetaPixelEnabled && !window.fbq) {
            // Concise arrow captures fbq by closure; queue is populated before fbq() is ever called.
            const fbq = ((...args: unknown[]) => fbq.queue.push(args)) as unknown as MetaPixelInterface
            fbq.queue = []
            fbq.loaded = true
            fbq.version = '2.0'
            window.fbq = fbq
            ;(window as Window & {_fbq?: MetaPixelInterface;})._fbq = fbq
        }

        if (config.isClarityEnabled && !window.clarity) {
            // Initialize the clarity function queue if it doesn't exist yet
            window.clarity = window.clarity ?? function (...args: unknown[]) {
                window.clarity!.q = window.clarity!.q ?? []
                window.clarity!.q.push(args)
            }
        }
    }

    // Инициализация сервиса.
    static init(config: Partial<AnalyticsServiceConfig>): void {
        this.config = {...defaultConfig, ...config}

        // Для Google Analytics.
        this.addGoogleAnalyticsCookie()
    }

    // Отправить ID пользователя в Google Analytics.
    static sendUserIdToGoogleAnalytics(userId: number | string | null): void {
        this.safelySendToGoogleAnalytics('set', {user_id: String(userId)})
    }

    // Отслеживание перемещения между страницами.
    static onUrlChange(absoluteUrl: string): void {
        if (this.lastUrl !== absoluteUrl) {
            const prevUrl = this.lastUrl
            this.lastUrl = absoluteUrl
            this.reportUrlChange(absoluteUrl, prevUrl)
        }
    }

    // Сохранить трекинг ID для Google Analytics в Cookies.
    private static addGoogleAnalyticsCookie(): void {
        const trackingId: string | null = TrackingService.getTrackingId()
        if (trackingId) {
            document.cookie = this.config.googleAnalyticsCookieName + '=' + trackingId
                + ';max-age=' + this.config.googleAnalyticsCookieLifeTime
                + ';path=/'
        }
    }

    // Отправить событие hit в Google Analytics, Яндекс Метрику и Meta Pixel.
    private static reportUrlChange(absoluteUrl: string, _prevUrl?: string | null): void {
        // Yandex Metrika.
        // if (this.config.yandexMetrikaClientId) {
        //     try {
        //         this.runWhenYandexMetrikaReady(ymInstance => {
        //             ymInstance(this.config.yandexMetrikaClientId!, 'hit', absoluteUrl, {
        //                 referer: prevUrl ?? undefined,
        //             })
        //         })
        //     } catch (e) {
        //         console.error('Error sending hit to Yandex Metrika', e)
        //     }
        // }

        // Google Analytics.
        if (this.config.googleAnalyticsAutoTracking) {
            this.safelySendToGoogleAnalytics('event', 'page_view', {
                page_title: window.document.title,
                page_location: absoluteUrl,
                client_id: TrackingService.getTrackingId(),
                user_agent: window.navigator.userAgent,
            })
        }

        // Meta Pixel.
        this.safelySendToMetaPixel('PageView')

        // PostHog. Экономим события.
        // posthog.capture('page_view', {
        //     page_title: window.document.title,
        //     page_location: absoluteUrl,
        //     user_agent: window.navigator.userAgent,
        // })
    }

    // Отправка данных в Google Analytics.
    static safelySendToGoogleAnalytics<Command extends keyof GtagCommands>(
        command: Command,
        ...args: GtagCommands[Command]
    ): void {
        try {
            if (
                this.config.googleAnalyticsClientId
                && typeof gtag === 'function'
            ) {
                return gtag<Command>(command, ...args)
            }
        } catch (e) {
            console.error('Error sending event to Google Analytics', e, {
                command,
                args,
            })
        }
    }

    // Отправка события в Google Analytics.
    static safelySendEventToGoogleAnalytics(
        eventName: EventNames | string,
        eventData?: GtagEventData,
        eventId?: string
    ): void {
        if (eventId) {
            eventData = {
                ...(eventData ?? {}),
                event_id: eventId,
            }
        }
        this.safelySendToGoogleAnalytics('event', eventName, eventData)
    }

    // Отправить событие в Meta Pixel.
    static safelySendToMetaPixel(
        event: MetaPixelStandardEvent,
        eventData?: MetaPixelEventProperties
    ): void {
        if (!this.config.isMetaPixelEnabled || this.isMetaPixelLoaded === false) {
            return
        }
        try {
            if (typeof window.fbq === 'function') {
                window.fbq('track', event, eventData)
            } else {
                this.runWhenMetaPixelReady(
                    () => this.safelySendToMetaPixel(event, eventData)
                )
            }
        } catch (e) {
            console.error('Error sending event to Meta Pixel', e, {
                event,
                eventData,
            })
        }
    }

    // Отправить событие в Meta Pixel.
    static safelySendEventToMetaPixel(
        event: MetaPixelStandardEvent,
        eventData?: MetaPixelEventProperties
    ): void {
        this.safelySendToMetaPixel(event, eventData)
    }

    // Отправить событие в Amplitude.
    static safelySendEventToAmplitude(
        eventInput: string | BaseEvent,
        eventData?: AnyObject,
        eventOptions?: EventOptions
    ): void {
        try {
            if (!this.config.isAmplitudeEnabled) {
                return
            }
            amplitude.track(eventInput, eventData, eventOptions)
        } catch (e) {
            console.error('Error sending event to Amplitude', e, {
                eventInput,
                eventData,
                eventOptions,
            })
        }
    }

    // Отправить ID пользователя в Amplitude.
    static safelyIdentifyUserInAmplitude(
        userId: string | number | null,
        userProperties?: AnyObject
    ): void {
        try {
            if (!this.config.isAmplitudeEnabled) {
                return
            }
            amplitude.setUserId(userId ? String(userId) : undefined)
            const identifyObj = new amplitude.Identify()
            if (userProperties) {
                for (const entry of Object.entries(userProperties)) {
                    identifyObj.set(entry[0], entry[1] as string)
                }
            }
            amplitude.identify(identifyObj)
        } catch (e) {
            console.error('Error sending user info to Amplitude', e, {
                userId,
                userProperties,
            })
        }
    }

    // Установить плагин Session Replay для Amplitude.
    static setSessionReplayHandler(handler: SessionReplayPlugin): void {
        this.amplitudeSessionReplayPlugin = handler
    }

    /**
     * Запустить запись Session Replay в Amplitude вручную.
     * По умолчанию запись отключена (sampleRate: 0).
     * Вызывайте этот метод при наступлении нужных событий, чтобы начать запись.
     */
    static safelyStartAmplitudeSessionReplay(): void {
        try {
            const plugin = this.amplitudeSessionReplayPlugin
            if (!this.config.isAmplitudeEnabled || !plugin || !plugin.config?.apiKey) {
                return
            }
            if (this.isSessionReplayRecording) {
                return
            }
            this.isSessionReplayRecording = true
            void plugin.sessionReplay.init(plugin.config.apiKey, {
                ...plugin.srInitOptions,
                sampleRate: 1,
            }).promise
        } catch (e) {
            console.error('Error starting Amplitude Session Replay', e)
        }
    }

    /**
     * Остановить запись Session Replay в Amplitude.
     */
    static safelyStopAmplitudeSessionReplay(): void {
        try {
            if (!this.config.isAmplitudeEnabled || !this.amplitudeSessionReplayPlugin) {
                return
            }
            if (!this.isSessionReplayRecording) {
                return
            }
            this.isSessionReplayRecording = false
            this.amplitudeSessionReplayPlugin.sessionReplay.shutdown()
        } catch (e) {
            console.error('Error stopping Amplitude Session Replay', e)
        }
    }

    // Отправить команду в Microsoft Clarity.
    static safelySendToClarity(
        command: 'set' | 'event' | 'identify' | 'consent' | 'consentv2' | 'upgrade',
        ...args: unknown[]
    ): void {
        if (!this.config.isClarityEnabled || this.isClarityLoaded === false) {
            return
        }
        try {
            if (typeof window.clarity === 'function') {
                window.clarity(command, ...args)
            } else {
                this.runWhenClarityReady(
                    () => this.safelySendToClarity(command, ...args)
                )
            }
        } catch (e) {
            console.error('Error sending event to Clarity', e, {
                command,
                args,
            })
        }
    }

    // Отправить событие в Microsoft Clarity.
    static safelySendEventToClarity(
        eventName?: string,
        eventData?: AnyObject
    ): void {
        this.safelySendToClarity('event', eventName, eventData)
    }

    // Выполнить callback, когда MS Clarity будет готова к отправке событий.
    static runWhenClarityReady(callback: () => void, attemptsLeft: number = 20): void {
        if (this.isClarityLoaded === false) {
            return
        }
        if (typeof window.clarity === 'function') {
            this.isClarityLoaded = true
            callback()
        } else if (attemptsLeft > 0) {
            setTimeout(
                () => AnalyticsService.runWhenClarityReady(callback, attemptsLeft - 1),
                500
            )
        } else {
            this.isClarityLoaded = false
            console.error('Clarity not loaded after 20 attempts (10 seconds)')
        }
    }

    // Выполнить callback, когда Meta Pixel будет готова к отправке событий.
    static runWhenMetaPixelReady(callback: () => void, attemptsLeft: number = 20): void {
        if (this.isMetaPixelLoaded === false) {
            return
        }
        if (typeof window.fbq === 'function') {
            callback()
        } else if (attemptsLeft > 0) {
            setTimeout(
                () => AnalyticsService.runWhenMetaPixelReady(callback, attemptsLeft - 1),
                500
            )
        } else {
            this.isMetaPixelLoaded = false
            console.error('Meta Pixel not loaded after 20 attempts (10 seconds)')
        }
    }

    // Выполнить callback, когда Яндекс Метрика будет готова к отправке событий.
    // static runWhenYandexMetrikaReady(
    //     callback: (ymInstance: typeof window.ym) => void,
    //     attemptsLeft: number = 20
    // ): void {
    //     if (this.isYandexMetrikaLoaded === false) {
    //         return
    //     }
    //     if (typeof window.ym === 'function') {
    //         this.isYandexMetrikaLoaded = true
    //         callback(window.ym)
    //     } else if (attemptsLeft > 0) {
    //         setTimeout(
    //             () => AnalyticsService.runWhenYandexMetrikaReady(callback, attemptsLeft - 1),
    //             500
    //         )
    //     } else {
    //         this.isYandexMetrikaLoaded = false
    //         console.error('Yandex Metrika not loaded after 20 attempts (10 seconds)')
    //     }
    // }
}
